import { useState } from "react";
import { ApprovalModule } from "../types";
import {
  useCreateDelegation,
  useDelegations,
  useDeleteDelegation,
} from "../hooks";
import { modules, moduleLabel } from "../utils";
import { DelegationTable } from "../components/DelegationTable";
import { useConfirm } from "#/components/common/ConfirmProvider";

export default function WorkflowDelegationsPage() {
  const confirm = useConfirm();
  const [moduleCode, setModuleCode] = useState<ApprovalModule | "">("");
  const { data = [], isLoading } = useDelegations(moduleCode);

  const create = useCreateDelegation();
  const remove = useDeleteDelegation();

  const [form, setForm] = useState({
    delegatorUserId: "",
    delegateUserId: "",
    moduleCode: "LEAVE" as ApprovalModule,
    startDate: "",
    endDate: "",
    reason: "",
    active: true,
    autoReassignPending: true,
  });

  async function submit() {
    await create.mutateAsync({
      delegatorUserId: Number(form.delegatorUserId),
      delegateUserId: Number(form.delegateUserId),
      moduleCode: form.moduleCode,
      startDate: form.startDate,
      endDate: form.endDate,
      reason: form.reason,
      active: form.active,
      autoReassignPending: form.autoReassignPending,
    });

    setForm({
      ...form,
      delegatorUserId: "",
      delegateUserId: "",
      reason: "",
    });
  }

  async function deleteOne(id: number) {
    if (!(await confirm("Delete this delegation?"))) return;
    await remove.mutateAsync(id);
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Workflow Delegations</h1>
        <p className="text-sm text-slate-500">
          Configure temporary approver delegation and automatic reassignment.
        </p>
      </div>

      <div className="rounded-2xl border bg-white p-5">
        <h3 className="font-bold">Create Delegation</h3>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          <Field label="Delegator User ID">
            <input
              className="input"
              value={form.delegatorUserId}
              onChange={(e) =>
                setForm({ ...form, delegatorUserId: e.target.value })
              }
            />
          </Field>

          <Field label="Delegate User ID">
            <input
              className="input"
              value={form.delegateUserId}
              onChange={(e) =>
                setForm({ ...form, delegateUserId: e.target.value })
              }
            />
          </Field>

          <Field label="Module">
            <select
              className="input"
              value={form.moduleCode}
              onChange={(e) =>
                setForm({ ...form, moduleCode: e.target.value as ApprovalModule })
              }
            >
              {modules.map((m) => (
                <option key={m} value={m}>{moduleLabel(m)}</option>
              ))}
            </select>
          </Field>

          <Field label="Start Date">
            <input
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) =>
                setForm({ ...form, startDate: e.target.value })
              }
            />
          </Field>

          <Field label="End Date">
            <input
              type="date"
              className="input"
              value={form.endDate}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </Field>

          <Field label="Reason">
            <input
              className="input"
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
            />
          </Field>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="flex items-center gap-2 rounded-xl border p-3 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active
          </label>

          <label className="flex items-center gap-2 rounded-xl border p-3 text-sm">
            <input
              type="checkbox"
              checked={form.autoReassignPending}
              onChange={(e) =>
                setForm({
                  ...form,
                  autoReassignPending: e.target.checked,
                })
              }
            />
            Auto reassign pending items
          </label>
        </div>

        <button
          onClick={submit}
          className="mt-4 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"
        >
          Create Delegation
        </button>
      </div>

      <div className="rounded-2xl border bg-white p-4">
        <select
          className="input max-w-xs"
          value={moduleCode}
          onChange={(e) => setModuleCode(e.target.value as ApprovalModule | "")}
        >
          <option value="">All Modules</option>
          {modules.map((m) => (
            <option key={m} value={m}>{moduleLabel(m)}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p>Loading delegations...</p>
      ) : (
        <DelegationTable delegations={data} onDelete={deleteOne} />
      )}
    </div>
  );
}

function Field({ label, children }: any) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}